/**
 * TROPAC Market Price Sort
 *
 * Re-orders the collection grid by displayed local-currency price for NZ/UK
 * markets. Shopify's native sort_by=price-ascending uses the shop's base
 * currency (AUD), which produces incorrect order when market price overrides
 * exist (e.g. a product that sells for double in NZ still sorts by its AUD
 * baseline). This module reads the market-aware price from the DOM
 * (data-tropac-price attribute, set by price.liquid) and re-sorts the grid.
 *
 * Option 1: client-side re-sort of the current page only. Cross-page order
 * is NOT fixed (a cheaper item on page 2 won't move to page 1). This is the
 * known limitation — acceptable for most collections where page 1 is the
 * primary visible surface.
 *
 * Owned asset — see theme/CUSTOMISATIONS.md
 * Activated only for NZ/UK markets (gated by the Liquid snippet).
 */
(function () {
  'use strict';

  var config = window.TROPAC_MARKET_PRICE_SORT;
  if (!config || !config.enabled) return;

  var GRID_SELECTOR = '#product-grid';
  var ROWS_SELECTOR = '#product-rows';
  var CONTAINER_SELECTOR = '#ProductGridContainer';

  /**
   * Get the active sort key from the URL, falling back to the collection's
   * configured default.
   */
  function getCurrentSort() {
    var params = new URLSearchParams(window.location.search);
    return params.get('sort_by') || config.defaultSort || '';
  }

  function isPriceSort(key) {
    return key === 'price-ascending' || key === 'price-descending';
  }

  /**
   * Read the price (integer cents, market-aware) from a grid/row item's
   * [data-tropac-price] attribute. Returns null for items without the
   * attribute (inline banners, call-for-pricing, login-for-price, free).
   */
  function getPrice(el) {
    var node = el.querySelector('[data-tropac-price]');
    if (!node) return null;
    var v = parseInt(node.getAttribute('data-tropac-price'), 10);
    return isNaN(v) ? null : v;
  }

  /**
   * Sort the grid in place. Partitions children into "sortable" (have a
   * price) and "fixed" (banners, etc.). Only sortable items are reordered;
   * fixed items stay in their original position so inline banners don't move.
   */
  function sortGrid() {
    var sortKey = getCurrentSort();
    if (!isPriceSort(sortKey)) return;

    var grid = document.querySelector(GRID_SELECTOR) || document.querySelector(ROWS_SELECTOR);
    if (!grid || grid.children.length < 2) return;

    var ascending = sortKey === 'price-ascending';
    var children = Array.prototype.slice.call(grid.children);

    // Partition: collect sortable entries and mark slot positions
    var sortable = [];
    var slots = children.map(function (child) {
      var price = getPrice(child);
      if (price === null) {
        return { el: child, sortable: false };
      }
      var entry = { el: child, price: price };
      sortable.push(entry);
      return { el: child, sortable: true };
    });

    if (sortable.length < 2) return;

    // Skip if already sorted (prevents MutationObserver feedback loops)
    var alreadySorted = true;
    for (var i = 1; i < sortable.length; i++) {
      var prev = sortable[i - 1].price;
      var curr = sortable[i].price;
      if (ascending ? prev > curr : prev < curr) {
        alreadySorted = false;
        break;
      }
    }
    if (alreadySorted) return;

    // Sort the sortable entries
    sortable.sort(function (a, b) {
      return ascending ? a.price - b.price : b.price - a.price;
    });

    // Re-insert: fill sortable slots from the sorted list, leave fixed slots
    var si = 0;
    var fragment = document.createDocumentFragment();
    slots.forEach(function (slot) {
      fragment.appendChild(slot.sortable ? sortable[si++].el : slot.el);
    });
    grid.appendChild(fragment);
  }

  // Guard against re-entry + debounce via requestAnimationFrame
  var pending = false;

  function scheduleSort() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      sortGrid();
    });
  }

  // --- Activation ---

  // 1. Initial page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSort);
  } else {
    scheduleSort();
  }

  // 2. After AJAX filter/sort render (facets.js dispatches this event)
  document.addEventListener('collection:product-filters', scheduleSort);

  // 3. After pagination / any grid content swap (MutationObserver)
  var container = document.querySelector(CONTAINER_SELECTOR);
  if (container) {
    new MutationObserver(scheduleSort).observe(container, {
      childList: true,
      subtree: true,
    });
  }
})();
